import { html } from '/vendor/htm-preact.module.js';
import { useRead } from '/read.js';
import { KeepPage } from '/shortcuts.js';

/**
 * One competition: who entered it, and the knockout as it was played.
 *
 * GitHub issue #30. The Competitions room listed instances and their winners;
 * this is what opening one shows.
 *
 * THE BRACKET IS A RECORD, NOT A RECONSTRUCTION. Every cup fixture carries
 * the round the league itself stated — off the Match Hub's Competition cell,
 * which was being parsed and thrown away until this feature needed it. That
 * distinction is the whole reason this waited rather than shipping sooner:
 * rounds guessed from fixture dates are wrong in exactly the cases that
 * matter — a rearranged tie, a walkover played early — and a wrong bracket
 * looks identical to a right one.
 */
export function CupScreen({ leagueId, season, competition, onGo, onDone }) {
  // Query parameters, because a competition is called "Division 3/4 Singles
  // Knockout Main" and a slash cannot survive a path segment -- see the
  // endpoint's own docstring.
  const [cup, error] = useRead(
    `/api/leagues/${encodeURIComponent(leagueId)}/competition`
    + `?season=${encodeURIComponent(season)}`
    + `&competition=${encodeURIComponent(competition)}`);

  if (error) {
    return html`
      <div class="screen">
        <button class="secondary" onClick=${onDone}>Back</button>
        <p class="error" data-testid="cup-error">
          ${error.detail || error.message}
        </p>
      </div>`;
  }
  if (cup === undefined) return html`<p class="sub">Loading…</p>`;

  return html`
    <div class="screen" data-testid="cup-screen">
      <div class="rowbetween">
        <h2 data-testid="cup-name">${cup.competition}</h2>
        ${/* Issue #77, wrapped with Back for the reason team.js gives:
              `.rowbetween` expects two children. */''}
        <span class="row" style="width:auto">
          <${KeepPage} variant="plain" />
          <button class="secondary" style="width:auto"
                  data-testid="cup-back" onClick=${onDone}>Back</button>
        </span>
      </div>
      <p class="sub" data-testid="cup-season">${cup.season}</p>

      ${cup.winner && html`
        <div class="card rowbetween" data-testid="cup-winner">
          <span>${`★ ${cup.winner}`}</span>
          <span class="sub">Winner</span>
        </div>`}

      ${/* Everybody who appears on either side of any fixture. There is no
            entry list in the archive -- a cup's entrants are recorded only
            by their turning up in it, and the honours table names the
            winner alone. */''}
      <div class="stack" data-testid="cup-entrants">
        <label>${`${cup.entrants.length} `}
          ${cup.entrants.length === 1 ? 'entrant' : 'entrants'}</label>
        <p class="sub" style="text-transform:none"
           data-testid="cup-entrant-names">
          ${cup.entrants.join(' · ')}
        </p>
      </div>

      ${/* The bracket. Rounds as columns, earliest on the left, each tie
            beside the one it feeds -- which is what "who plays who next" is
            a question about, and what a stack of separate round lists could
            not answer however clearly each list was drawn. */''}
      ${cup.rounds.length > 0 && html`
        <div class="stack" data-testid="cup-bracket">
          <label>The draw</label>
          <div class="bracket">
            ${layoutBracket(cup.rounds).map((r) => html`
              <div class="bracket-col" key=${r.round}
                   data-testid=${`cup-round-${r.round.replace(/\s+/g, '-')}`}>
                <span class="cal-num">${r.round}</span>
                <div class="bracket-slots"
                     style=${`height:calc(${r.span} * var(--korow))`}>
                  ${/* The lines first, so a tie always sits over them. */''}
                  ${r.stubs.map((slot, i) => html`
                    <span class="bracket-stub" key=${`s${i}`}
                          style=${`top:calc((${slot} + .5) * var(--korow))`} />`)}
                  ${r.joins.map((j, i) => html`
                    <span class="bracket-join" key=${`j${i}`}
                          style=${`top:calc((${j.top} + .5) * var(--korow));`
                                  + `height:calc(${j.bottom - j.top} * var(--korow))`} />`)}
                  ${r.ties.map((t) => html`
                    <div class="bracket-slot" key=${t.fixture.fixture_id}
                         style=${`top:calc(${t.slot} * var(--korow))`}>
                      <${Tie} fixture=${t.fixture}
                              leagueId=${leagueId} onGo=${onGo} />
                    </div>`)}
                </div>
              </div>`)}
          </div>
        </div>`}

      ${/* Fixtures the archive never gave a round to -- every cup match
            scraped before the round was captured. Named for what they are
            rather than folded into the bracket: one phantom round holding
            fifteen years of matches would be worse than saying so. */''}
      ${cup.unplaced.length > 0 && html`
        <div class="stack" data-testid="cup-unplaced">
          <label>Round not recorded</label>
          <p class="sub">
            ${`${cup.unplaced.length} `}
            ${cup.unplaced.length === 1 ? 'match was' : 'matches were'}
            ${' '}played before the league's round was being captured.
          </p>
          ${cup.unplaced.map((f) => html`
            <${Tie} key=${f.fixture_id} fixture=${f} leagueId=${leagueId}
                    onGo=${onGo} />`)}
        </div>`}
    </div>`;
}


/**
 * WHERE EVERY TIE SITS, computed rather than guessed.
 *
 * A knockout's shape is not in the data -- the archive records fixtures, not
 * a tree -- but it is DERIVABLE: the winner of a tie turns up in the next
 * round, so the tie that produced somebody is the one they last won.
 *
 * The three attempts before this one ORDERED the ties correctly and left the
 * vertical position to CSS, which is why they never lined up. Ben said so
 * three times. The reasons were mundane and unfixable in percentages:
 *
 *   * the round's label was a flex child of its column, so `space-around`
 *     shared the height between FIVE things in an eight-tie round and THREE
 *     in a four-tie one -- different slot sizes, so nothing could align;
 *   * a gap in rem between ties is a fixed distance that does not scale with
 *     the number of them, so each column drifted by a different amount;
 *   * and a ragged round -- a bye, a preliminary, a walkover -- puts a
 *     different number of ties in each column, which no fixed percentage can
 *     describe.
 *
 * So the position is arithmetic now. Each tie gets a SLOT, in units of one
 * tie's height, and CSS only turns a slot into a distance:
 *
 *   * the first round's ties take slots 0, 1, 2, ... in draw order;
 *   * every later tie sits at the MEAN of the slots of the ties feeding it,
 *     which is what "centred between its two feeders" means, and stays true
 *     when there is one feeder, or three, or none;
 *   * a tie nothing feeds -- an unplayed round, a withdrawal -- goes below
 *     what is placed rather than being hidden or invented a position.
 *
 * The lines come from the same numbers, so they cannot disagree with the
 * boxes: a stub at each feeder's middle, a vertical spanning the feeders, and
 * the onward stub at the vertical's midpoint -- which IS the parent's slot,
 * because the parent's slot is their mean.
 *
 * Deliberately not a strict binary tree. A real cup has byes, walkovers,
 * preliminary rounds of odd size and ties still unplayed, and a layout that
 * insisted on 2^n would have to invent or drop something to fit.
 */
function layoutBracket(rounds) {
  // THE LEAGUE'S OWN CHART, where we have read one (issue #30). Each tie
  // arrives carrying the slot it sits at and the slot it feeds, because
  // LeagueRepublic drew it that way and a record beats a reconstruction --
  // it has the byes, the walkovers and the rounds drawn but not yet played,
  // none of which can be worked out from results.
  //
  // Everything below this branch is the fallback for a competition whose
  // chart we have not read, and is unchanged.
  if (rounds.length && rounds[0].fixtures.length
      && typeof rounds[0].fixtures[0].slot === 'number') {
    return rounds.map((r) => {
      const ties = r.fixtures
        .map((f) => ({ fixture: f, slot: f.slot }))
        .sort((a, b) => a.slot - b.slot);
      const stubs = ties.filter((t) => t.fixture.feeds != null)
        .map((t) => t.slot);
      const grouped = new Map();
      ties.forEach((t) => {
        if (t.fixture.feeds == null) return;
        if (!grouped.has(t.fixture.feeds)) grouped.set(t.fixture.feeds, []);
        grouped.get(t.fixture.feeds).push(t.slot);
      });
      const joins = [];
      grouped.forEach((slots) => {
        const sorted = slots.slice().sort((a, b) => a - b);
        joins.push({ top: sorted[0], bottom: sorted[sorted.length - 1] });
      });
      return {
        round: r.round, ties, stubs, joins,
        span: ties.reduce((m, t) => Math.max(m, t.slot), 0) + 1,
      };
    });
  }

  // Where each fixture's winner goes: fixture_id -> [tie index in the next
  // round, which side of it]. The side is the tiebreak between two feeders of
  // one tie: the one whose winner is listed home belongs on top, so the pair
  // reads down into the tie it makes.
  const seats = [];
  for (let i = 0; i < rounds.length - 1; i += 1) {
    const goesTo = new Map();
    rounds[i + 1].fixtures.forEach((f, at) => {
      ['home', 'away'].forEach((side, which) => {
        if (f[side] && f[side].name) goesTo.set(f[side].name, [at, which]);
      });
    });
    const here = new Map();
    rounds[i].fixtures.forEach((f) => {
      const s = f.score;
      const won = !s ? null
        : s.home > s.away ? f.home.name
        : s.away > s.home ? f.away.name : null;
      const found = won != null ? goesTo.get(won) : undefined;
      // Unplaceable ties sort last and feed nothing.
      here.set(f.fixture_id, found === undefined
        ? [Number.MAX_SAFE_INTEGER, 0] : found);
    });
    seats.push(here);
  }

  const out = [];
  for (let i = 0; i < rounds.length; i += 1) {
    const seat = seats[i - 1];
    let ties;
    if (!seat) {
      // The first round. Its order IS the draw order, and every later
      // position descends from it, so it is ordered by where its winners go.
      const by = seats[0];
      const order = rounds[i].fixtures
        .map((f, at) => ({ f, at, s: by ? by.get(f.fixture_id) : [at, 0] }))
        .sort((a, b) => (a.s[0] - b.s[0]) || (a.s[1] - b.s[1]) || (a.at - b.at));
      ties = order.map((x, k) => ({ fixture: x.f, slot: k, feeders: [] }));
    } else {
      // Everything after: the mean of whoever fed it.
      const prev = out[i - 1].ties;
      const byTie = new Map();
      prev.forEach((t) => {
        const [tie] = seat.get(t.fixture.fixture_id);
        if (tie === Number.MAX_SAFE_INTEGER) return;
        if (!byTie.has(tie)) byTie.set(tie, []);
        byTie.get(tie).push(t.slot);
      });
      const placed = rounds[i].fixtures.map((f, at) => {
        const feeders = (byTie.get(at) || []).slice().sort((a, b) => a - b);
        const slot = feeders.length
          ? feeders.reduce((a, b) => a + b, 0) / feeders.length : null;
        return { fixture: f, slot, feeders, at };
      });
      // Below everything already placed, in the order they arrived.
      let free = placed.reduce((m, t) => Math.max(m, t.slot === null ? -1 : t.slot),
                               prev.reduce((m, t) => Math.max(m, t.slot), -1)) + 1;
      placed.forEach((t) => { if (t.slot === null) { t.slot = free; free += 1; } });
      ties = placed.sort((a, b) => (a.slot - b.slot) || (a.at - b.at));
    }

    // The lines belong to the column whose ties they leave, and are drawn
    // from the same slots as the boxes -- so they cannot drift apart.
    const stubs = [];
    const joins = [];
    const nextSeat = seats[i];
    if (nextSeat) {
      const byTie = new Map();
      ties.forEach((t) => {
        const [tie] = nextSeat.get(t.fixture.fixture_id);
        if (tie === Number.MAX_SAFE_INTEGER) return;
        if (!byTie.has(tie)) byTie.set(tie, []);
        byTie.get(tie).push(t.slot);
        stubs.push(t.slot);
      });
      byTie.forEach((slots) => {
        const s = slots.slice().sort((a, b) => a - b);
        // A lone feeder -- a bye, a walkover -- gets a zero-height vertical,
        // which draws as the straight line onward that it actually is.
        joins.push({ top: s[0], bottom: s[s.length - 1] });
      });
    }

    out.push({
      round: rounds[i].round,
      ties,
      stubs,
      joins,
      span: ties.reduce((m, t) => Math.max(m, t.slot), 0) + 1,
    });
  }
  return out;
}

/**
 * One tie. The winner's name is emphasised rather than marked with a tick:
 * a knockout is read down the page for who went through, and the eye needs
 * the name, not a symbol beside it.
 *
 * A tie with no score is one still to be played, and says so rather than
 * showing "0–0", which is a result somebody could have got.
 */
function Tie({ fixture, leagueId, onGo }) {
  const score = fixture.score;
  // Present only on a tie read off the league's chart, and only meaningful
  // when at least one side carries something.
  const marks = fixture.marks && (fixture.marks.home || fixture.marks.away)
    ? fixture.marks : null;
  const homeWon = score && score.home > score.away;
  const awayWon = score && score.away > score.home;

  // ONE NAME A LINE, and the venue on its own beneath them (Ben, on issue
  // #30: "the format is awful, text is over one another, please move venue
  // on to its own line").
  //
  // The first version put both names and the score on one row and the venue
  // beside it. That reads fine on "Ann v Bob at The Academy" and not at all
  // on what the archive actually holds -- "Paul Smurf Richardson" against
  // "Wellington Social Club Table 1 (By the Bar)" -- which is what the
  // fixture in the test now uses, so the layout is exercised at the width
  // real data has.
  //
  // It is also how the league draws its own chart: two stacked rows, each a
  // name and its score, then the venue and date under them.
  //
  // Emphasis by WEIGHT, not a tick beside the name: a knockout is read down
  // the page for who went through, and the eye wants the name itself. An
  // unplayed tie emphasises neither -- there is no winner yet, and bolding
  // one would be a prediction.
  const line = (who, won, mark) => html`
    <span class="korow">
      <span class=${won ? 'koname' : 'koname sub'}
            style=${won ? 'font-weight:650' : ''}>${who.name || '—'}</span>
      <span class="mono pts">${mark}</span>
    </span>`;

  // A tie with MARKS but no score is a walkover, not a tie still to be
  // played -- the league writes letters where a score goes ("H" and "W"),
  // and reading that as "to play" says the opposite of what happened. The
  // marks are printed as the league wrote them rather than translated.
  const homeMark = score ? score.home : marks ? marks.home : '';
  const awayMark = score ? score.away : marks ? marks.away : '';

  // A chart names ties that have no match page behind them yet -- a round
  // drawn but unplayed, a bye. Those are shown and are NOT pressable: a
  // button that navigates to nothing is worse than a box that does not
  // invite the press.
  const openable = fixture.linked !== false;
  return html`
    <button class=${`secondary card kotie${openable ? '' : ' flat'}`}
            data-testid=${`cup-tie-${fixture.fixture_id}`}
            disabled=${!openable}
            onClick=${() => openable
                            && onGo('fixture', leagueId, fixture.fixture_id)}>
      ${line(fixture.home, homeWon, homeMark)}
      ${line(fixture.away, awayWon, awayMark)}
      ${!score && !marks && html`
        <span class="sub kometa">to play</span>`}
      ${/* Where THIS tie is played, on its own line. It belongs to the
            fixture and not to the round -- every tie in a round has one and
            they differ, which is why it was wrong in the round's name. */''}
      ${fixture.venue && html`
        <span class="sub kometa" data-testid=${`cup-venue-${fixture.fixture_id}`}>
          ${fixture.venue}
        </span>`}
    </button>`;
}

